import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUp, ExternalLink, Loader2, Newspaper, TrendingUp, TrendingDown } from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import MarketClosedBanner from '@/components/MarketClosedBanner';
import NewsWatchStar from '@/components/NewsWatchStar';
import NewsArticleImage from '@/components/NewsArticleImage';
import {
  ProviderSentimentChip,
  NewsSentimentSummary,
  MassiveInsightReasoning,
} from '@/components/news/NewsBadges';
import SiteDisclaimer from '@/components/SiteDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Never cached — matches the leaderboard's live top-10 rhythm.
const POLL_MS = 60_000;

// Session-scoped cache so the Back button / route-return doesn't
// re-fetch 40-60 tickers of news just to show the exact same
// content the user was looking at 30 seconds ago. Only lives for
// the current tab session and expires after 5 minutes so a stale
// list can't linger forever.
const CACHE_KEY = 'dividrip:newsPage:cache';
const CACHE_TTL_MS = 5 * 60_000;

function readNewsCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}

function writeNewsCache(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, savedAt: Date.now() }));
  } catch { /* quota exceeded or private mode — ignore */ }
}

/**
 * Content-equality check: same kept ticker list AND same article count
 * per ticker → treat as unchanged and skip the re-render. If a ticker
 * dropped off the movers, joined it, or picked up a new article,
 * counts differ and we re-render.
 */
function isSameNewsContent(a, b) {
  if (!a || !b) return false;
  const at = (a.kept_tickers || []).join(',');
  const bt = (b.kept_tickers || []).join(',');
  if (at !== bt) return false;
  const abt = a.by_ticker || {};
  const bbt = b.by_ticker || {};
  for (const t of a.kept_tickers || []) {
    if ((abt[t]?.count || 0) !== (bbt[t]?.count || 0)) return false;
  }
  return true;
}

// Oct 2026 — the ticker whose section currently owns the pinned slot
// (the section spanning the sticky ticker-bar line).
function currentPinnedTicker(tickerTop) {
  const probeY = (tickerTop || 0) + 2;
  const els = document.querySelectorAll('[data-testid^="news-ticker-section-"]');
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.top <= probeY && r.bottom > probeY) {
      return el.getAttribute('data-testid').replace('news-ticker-section-', '');
    }
  }
  return null;
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function timeAgoShort(ts) {
  if (!ts) return '';
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function fmtPrice(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toFixed(2);
}

function QuoteBadge({ quote }) {
  if (!quote || !(Number(quote.price) > 0)) return null;
  const chg = Number(quote.change_pct);
  const up = isFinite(chg) && chg >= 0;
  const cls = up
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-rose-600 dark:text-rose-400';
  const Arrow = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-mono">
      {Number(quote.prev_close) > 0 && (
        <>
          <span className="text-muted-foreground" title="Previous close — the % change is measured against this">PC:</span>
          <span className="font-semibold">${fmtPrice(quote.prev_close)}</span>
        </>
      )}
      {Number(quote.open) > 0 && !quote.open_is_prev_close && (
        <>
          <span className="text-muted-foreground" title="Today's open">OP:</span>
          <span className="font-semibold">${fmtPrice(quote.open)}</span>
        </>
      )}
      <span className="text-muted-foreground">P:</span>
      <span className="font-semibold">${fmtPrice(quote.price)}</span>
      {isFinite(chg) && (
        <span className={`inline-flex items-center gap-0.5 font-bold ${cls}`}>
          <Arrow className="w-3.5 h-3.5" />
          {up ? '+' : ''}{chg.toFixed(2)}%
        </span>
      )}
    </span>
  );
}

function ArticleCard({ a, branding }) {
  return (
    <a
      href={a.url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-lg border bg-card hover:border-primary/50 transition-colors p-3 group"
      data-testid={`news-article-${a.ticker}-${a.provider}`}
    >
      <div className="flex gap-3">
        <NewsArticleImage
          article={a}
          branding={branding}
          variant="card"
          className="w-24 h-24 rounded flex-shrink-0 hidden sm:flex"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <ProviderSentimentChip provider={a.provider} sentiment={a.sentiment} />
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted">{a.publisher}</span>
            <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-1">
              {fmtDate(a.published_utc)}
              <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </div>
          <h3 className="text-sm font-bold leading-snug group-hover:text-primary transition-colors">
            {a.title}
          </h3>
          {a.description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{a.description}</p>
          )}
          <MassiveInsightReasoning article={a} />
          {a.author && (
            <p className="text-[10px] text-muted-foreground/70 mt-1">by {a.author}</p>
          )}
        </div>
      </div>
    </a>
  );
}

// Oct 2026 — build the gainers / losers sections (page order) once so the
// "News Tickers list" dropdown mirrors exactly what is rendered.
// divMode: 'all' | 'div' | 'nondiv' — filters on quotes[t].is_dividend.
function buildSections(data, divMode) {
  if (!data) return [];
  const keptSet = new Set(data.kept_tickers || []);
  const quotes = data.quotes || {};
  const passes = (t) => {
    if (divMode === 'all') return true;
    const isDiv = quotes[t]?.is_dividend === true;
    return divMode === 'div' ? isDiv : !isDiv;
  };
  const already = new Set();
  const defs = [
    { key: 'gainers', label: 'Top Market Gainers', cls: 'text-emerald-600 dark:text-emerald-400',
      src: [...(data.gainer_tickers || []), ...(data.massive_gainer_tickers || [])] },
    { key: 'losers', label: 'Top Market Losers', cls: 'text-rose-600 dark:text-rose-400',
      src: [...(data.loser_tickers || []), ...(data.massive_loser_tickers || [])] },
  ];
  return defs.map((section) => {
    const seen = new Set();
    const tickers = section.src.filter((t) => {
      if (!t || already.has(t) || seen.has(t) || !keptSet.has(t) || !passes(t)) return false;
      seen.add(t);
      return true;
    });
    tickers.forEach((t) => already.add(t));
    return { ...section, tickers };
  }).filter((sec) => sec.tickers.length > 0);
}

const DIV_MODES = [
  { key: 'div', label: 'Div', tip: 'Only dividend-paying stocks' },
  { key: 'all', label: 'All', tip: 'Every mover' },
  { key: 'nondiv', label: 'Non-Div', tip: 'Only stocks that pay no dividend' },
];

export default function News() {
  // Oct 2026 — Div / All / Non-Div switch (sticks across refreshes & visits).
  const [divMode, setDivMode] = useState(() => {
    try { const v = localStorage.getItem('news-div-mode'); return ['div', 'all', 'nondiv'].includes(v) ? v : 'all'; } catch { return 'all'; }
  });
  const pickDivMode = (m) => { setDivMode(m); try { localStorage.setItem('news-div-mode', m); } catch { /* private mode */ } };
  // Oct 2026 — background refresh anchor: remember the ticker whose bar is
  // pinned when a refresh lands, then put that ticker back in the slot.
  const anchorRef = useRef(null);
  const tickerTopRef = useRef(64);
  // Hydrate synchronously from the session cache so navigating back
  // doesn't paint the spinner + 3-5s of blank space when we already
  // have a fresh-enough version of the page.
  const cachedOnMount = typeof window !== 'undefined' ? readNewsCache() : null;
  const [data, setData] = useState(cachedOnMount ? cachedOnMount.data : null);
  // Sep 2026 — /news#ticker-XYZ (from the Top Movers "News" badge): once
  // the articles are in, scroll that ticker's section under the header.
  const { hash } = useLocation();
  useEffect(() => {
    const m = /^#ticker-([A-Z0-9.\-]+)$/i.exec(hash || '');
    if (!m || !data?.articles?.length) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="news-ticker-section-${m[1].toUpperCase()}"]`);
      if (!el) return;
      const hdr = document.querySelector('header');
      const y = el.getBoundingClientRect().top + window.scrollY - ((hdr?.getBoundingClientRect().height || 0) + 12);
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(t);
  }, [hash, data]);
  const [loading, setLoading] = useState(!cachedOnMount);
  const [stickyOffset, setStickyOffset] = useState(64);
  // Oct 2026 — desktop only: the page title row and the "Top Market
  // Gainers / Losers" heading stay pinned too, so the ticker bar locks
  // in the slot where the FIRST ticker bar sits and nothing above it
  // moves. Phones keep the original single sticky ticker bar.
  const titleRef = useRef(null);
  const [titleH, setTitleH] = useState(0);
  const [sideH, setSideH] = useState(0);
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 640px)').matches : true));
  // Preserve the timestamp of the last ACTUAL fetch (from cache) so the
  // "Refreshed Xs ago" label doesn't reset when the background poll
  // returns the same data.
  const [refreshedAt, setRefreshedAt] = useState(
    cachedOnMount ? cachedOnMount.savedAt : null
  );
  const [, setNowTick] = useState(0);

  // Measure the app header height once and re-measure on resize so
  // the sticky ticker pill lands right below whatever chrome the
  // AppHeader has stacked (logo row + How-To row + Screener row).
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header');
      if (header) setStickyOffset(header.getBoundingClientRect().height);
      setIsDesktop(window.matchMedia('(min-width: 640px)').matches);
      if (titleRef.current) setTitleH(titleRef.current.getBoundingClientRect().height);
      const side = document.querySelector('[data-testid^="news-side-wrap-"]');
      if (side) setSideH(side.getBoundingClientRect().height);
    };
    measure();
    window.addEventListener('resize', measure);
    // Rerun after render settles (fonts, images may shift height).
    const t = setTimeout(measure, 600);
    return () => { window.removeEventListener('resize', measure); clearTimeout(t); };
  }, [loading, data]);
  const titleTop = stickyOffset;
  const sideTop = isDesktop ? stickyOffset + titleH : stickyOffset;
  const tickerTop = isDesktop ? stickyOffset + titleH + sideH : stickyOffset;
  tickerTopRef.current = tickerTop;

  const sections = buildSections(data, divMode);
  const orderedTickers = sections.flatMap((sec) => sec.tickers);

  // Oct 2026 — Div / All / Non-Div switch + "News Tickers list" dropdown.
  // Rendered inside EVERY section heading row (gainers and losers) so the
  // controls ride along with whichever heading owns the pinned slot.
  const renderControls = (sectionKey) => (
    <>
      <div className="inline-flex items-center rounded-full border bg-muted/40 p-0.5" data-testid={`news-div-switch-${sectionKey}`} title="Show news for dividend payers, everything, or non-payers">
        {DIV_MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => pickDivMode(m.key)}
            aria-pressed={divMode === m.key}
            title={m.tip}
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${divMode === m.key ? 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-900' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`news-div-switch-${sectionKey}-${m.key}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      {orderedTickers.length > 0 && (
        <select
          value=""
          onChange={(e) => { if (e.target.value) scrollToTicker(e.target.value); e.target.value = ''; }}
          className="ml-auto h-7 max-w-[240px] sm:max-w-[360px] rounded-md border border-input bg-background px-2 text-[11px] font-medium text-foreground cursor-pointer"
          aria-label="News Tickers list"
          data-testid={`news-tickers-list-${sectionKey}`}
        >
          <option value="">News Tickers list ({orderedTickers.length})</option>
          {sections.map((sec) => (
            <optgroup key={sec.key} label={sec.label}>
              {sec.tickers.map((t) => (
                <option key={t} value={t}>
                  {t}{(data?.quotes || {})[t]?.name ? ` — ${data.quotes[t].name}` : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      )}
    </>
  );

  // Jump a ticker's section into the pinned slot (instant for refresh
  // restores, smooth for the dropdown).
  const scrollToTicker = (tkr, behavior = 'smooth') => {
    const el = document.querySelector(`[data-testid="news-ticker-section-${tkr}"]`);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - tickerTopRef.current;
    window.scrollTo({ top: Math.max(0, y), behavior });
  };
  useEffect(() => {
    if (!anchorRef.current) return undefined;
    const tkr = anchorRef.current;
    anchorRef.current = null;
    const t = setTimeout(() => scrollToTicker(tkr, 'auto'), 60);
    return () => clearTimeout(t);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    const url = `${API}/news/movers?use_top_movers=1&top_movers_limit=20&limit_per_ticker=50&_ts=`;
    const load = async () => {
      try {
        const res = await axios.get(url + Date.now(), { withCredentials: true });
        if (cancelled) return;
        const fresh = res.data;
        writeNewsCache(fresh);
        setData((prev) => {
          // Only reset the "Refreshed Xs ago" label when the payload
          // actually changed — otherwise silent background refresh
          // shouldn't feel like a page reload to the user.
          if (isSameNewsContent(prev, fresh)) return prev;
          if (prev) anchorRef.current = currentPinnedTicker(tickerTopRef.current);
          setRefreshedAt(Date.now());
          return fresh;
        });
        // First-ever load with no cache — still stamp so the counter starts.
        setRefreshedAt((prev) => prev ?? Date.now());
        setLoading(false);
      } catch {
        if (cancelled) return;
        // Keep any cached snapshot if the refresh fails; only fall
        // back to the disabled-shell if we truly have nothing.
        setData((prev) => prev || { enabled: false, articles: [], by_ticker: {}, by_publisher: {} });
        setLoading(false);
      }
    };
    load();
    const pollId = setInterval(load, POLL_MS);
    // Tick every second so "Refreshed Xs ago" counts up smoothly.
    const tickId = setInterval(() => setNowTick((n) => n + 1), 1_000);
    return () => { cancelled = true; clearInterval(pollId); clearInterval(tickId); };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-0" data-testid="news-page">
      <span id="news-top" aria-hidden />
      <div
        ref={titleRef}
        className="flex items-center gap-3 pb-4 flex-wrap sm:sticky sm:z-30 bg-background"
        style={isDesktop ? { top: titleTop } : undefined}
        data-testid="news-title-row"
      >
        <BackOrHomeButton testid="news-back-or-home-btn" />
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-primary" /> Market Movers News
        </h1>
        {refreshedAt && (
          <span className="text-[11px] text-muted-foreground/70 font-mono ml-auto"
                data-testid="news-refreshed"
                title={new Date(refreshedAt).toLocaleString()}>
            Refreshed {timeAgoShort(refreshedAt)}
          </span>
        )}
      </div>
      {loading && (
        <div className="flex items-center justify-center gap-3 py-16 text-2xl text-muted-foreground"
             data-testid="news-loading">
          <Loader2 className="w-7 h-7 animate-spin text-primary" />
          <span className="font-semibold">Loading…</span>
        </div>
      )}

      {!loading && data && (
        <>
          <div className="mb-4">
            <MarketClosedBanner note={data.market_note} testId="news-market-closed" />
          </div>
          {sections.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="news-div-empty">
              <div className="flex justify-center mb-3">{renderControls('empty')}</div>
              No {divMode === 'div' ? 'dividend-paying' : 'non-dividend'} movers with news right now — switch to <button type="button" className="underline font-semibold" onClick={() => pickDivMode('all')}>All</button>.
            </div>
          )}
          {/* Two sections: gainers and losers. Each section merges
              our dividend movers with Massive's raw market movers,
              deduped so a ticker only appears once. Within a ticker:
              Massive articles first (newest → oldest), then Yahoo. */}
          {(() => {
            return sections.map((section) => {
              const tickersHere = section.tickers;
              return (
                <div key={section.key} className="[&+&]:mt-8">
                  <div
                    className="pb-3 sm:sticky sm:z-20 bg-background"
                    style={isDesktop ? { top: sideTop } : undefined}
                    data-testid={`news-side-wrap-${section.key}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className={`text-xl font-bold ${section.cls}`} data-testid={`news-side-${section.key}`}>
                        {section.label}
                      </h2>
                      {renderControls(section.key)}
                    </div>
                  </div>
                  {tickersHere.map((tkr) => {
                    const meta = (data.by_ticker || {})[tkr] || { count: 0, publishers: {} };
                    const all = (data.articles || []).filter((a) => a.ticker === tkr);
                    // Sep 2026 — strictly newest → oldest regardless of
                    // provider (was Massive block first, then Yahoo, which
                    // put a fresh Yahoo story after older Massive ones).
                    const byDateDesc = (a, b) => (b.published_utc || '').localeCompare(a.published_utc || '');
                    const rows = [...all].sort(byDateDesc);
                    const isGainer = section.key.endsWith('gainers');
                    const accent = isGainer
                      ? 'from-emerald-500 to-emerald-600'
                      : 'from-rose-500 to-rose-600';
                    const ringAccent = isGainer
                      ? 'ring-emerald-500/30 hover:ring-emerald-500/60'
                      : 'ring-rose-500/30 hover:ring-rose-500/60';
                    const stickyBg = isGainer
                      ? 'bg-emerald-50/95 dark:bg-emerald-950/60'
                      : 'bg-rose-50/95 dark:bg-rose-950/60';
                    return (
                      <section
                        key={tkr}
                        className={`mt-4 first-of-type:mt-0 rounded-xl bg-slate-50 dark:bg-slate-900/40 ring-1 ${ringAccent} shadow-sm transition-shadow`}
                        data-testid={`news-ticker-section-${tkr}`}
                      >
                        {/* Sticky pill-style ticker header — locks
                            right beneath the AppHeader (measured on
                            mount) so it doesn't hide behind the app's
                            multi-row sticky nav. */}
                        <div
                          className={`sticky z-10 flex items-center gap-3 px-3 py-2 ${stickyBg} backdrop-blur border-b border-border/60 flex-wrap rounded-t-xl`}
                          style={{ top: tickerTop }}
                        >
                          <span
                            className={`inline-flex items-center gap-1 pl-1 pr-3 py-1 rounded-lg text-white text-lg font-black tracking-wide shadow-md bg-gradient-to-r ${accent} ring-1 ring-white/30`}
                            data-testid={`news-ticker-pill-wrap-${tkr}`}
                          >
                            <NewsWatchStar ticker={tkr} isDividend={(data.quotes || {})[tkr]?.is_dividend} />
                            <Link
                              to={`/stock/${tkr}`}
                              className="inline-flex items-center gap-1.5 rounded-md px-1.5 -my-0.5 py-0.5 hover:bg-white/15 hover:-translate-y-px active:translate-y-0 transition-[transform,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                              title={`Open the ${tkr} stock page`}
                              data-testid={`news-ticker-pill-${tkr}`}
                            >
                              {tkr}
                              <ExternalLink aria-hidden className="w-3.5 h-3.5 opacity-70" />
                            </Link>
                          </span>
                          {(() => {
                            const q = (data.quotes || {})[tkr];
                            if (!q || q.is_dividend === undefined) return null;
                            return q.is_dividend ? (
                              <span
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-700 dark:text-sky-300 uppercase tracking-wider"
                                data-testid={`news-ticker-divtag-${tkr}`}
                              >
                                Dividend
                              </span>
                            ) : (
                              <span
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 uppercase tracking-wider"
                                data-testid={`news-ticker-divtag-${tkr}`}
                              >
                                Non-Dividend
                              </span>
                            );
                          })()}
                          <QuoteBadge quote={(data.quotes || {})[tkr]} />
                          <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-mono">
                            {meta.count} article{meta.count === 1 ? '' : 's'}
                          </span>
                          <NewsSentimentSummary articles={rows} />
                        </div>
                        {/* Article rows:
                              • Phone (< sm): horizontal snap-scroll —
                                each card ~85% of viewport width, swipe
                                left/right to see more articles for this
                                ticker without leaving the section.
                              • Tablet+: stacked vertically (existing). */}
                        <div className="p-3 flex sm:flex-col gap-2 overflow-x-auto snap-x snap-mandatory sm:overflow-visible sm:snap-none scrollbar-thin"
                             data-testid={`news-ticker-rows-${tkr}`}>
                          {rows.map((a, i) => (
                            <div
                              key={i}
                              className="snap-start shrink-0 basis-[85%] sm:basis-auto sm:shrink"
                            >
                              <ArticleCard a={a} branding={(data.branding || {})[a.ticker]} />
                            </div>
                          ))}
                        </div>
                        <div className="px-3 pb-2 flex justify-end">
                          <a
                            href="#news-top"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline underline-offset-2 transition-colors"
                            data-testid={`news-back-to-top-${tkr}`}
                          >
                            <ArrowUp className="w-3 h-3" />
                            Back to top
                          </a>
                        </div>
                      </section>
                    );
                  })}
                </div>
              );
            });
          })()}
        </>
      )}
      <SiteDisclaimer />
    </div>
  );
}
